import React, { useEffect, useState } from 'react';
import { Modal, Table, Spin } from 'antd';
import { ProjectPaymentSummaryDto, MonthlyPaymentDto } from 'api/models';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import openNotificationWithIcon from 'utility/notification';
import { ContractorPaymentsApi } from 'api/api';
import { TableWrapper } from 'container/styled';
import { CustomTableStyleWrapper } from 'pages/style';

const SummaryTitle = styled.h3`
  text-align: center;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 20px;
  background-color: #fafafa;
`;

const SummaryItem = styled.div`
  text-align: center;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div<{ color: string }>`
  font-size: 24px;
  font-weight: bold;
  color: ${props => props.color};
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 256px;
`;

interface ContractorPaymentsSummaryModalProps {
  visible: boolean;
  onCancel: () => void;
  contractorId: number;
  projectId: number | null;
}

const contractorPaymentsApi = new ContractorPaymentsApi();

export const ContractorPaymentsSummaryModal: React.FC<ContractorPaymentsSummaryModalProps> = ({
  visible,
  onCancel,
  contractorId,
  projectId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [payments, setPayments] = useState<ProjectPaymentSummaryDto | null>(null);

  const fetchContractorPaymentsForProject = async (projectId: number) => {
    try {
      setLoading(true);
      const response = await contractorPaymentsApi.apiContractorsContractorIdPaymentsProjectProjectIdGet({
        contractorId: contractorId,
        projectId,
      });
      setPayments(response.data);
    } catch (error) {
      openNotificationWithIcon('error', t('payments:fetch-error', 'Error fetching payment details'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchContractorPaymentsForProject(projectId);
    }
  }, [projectId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!payments) {
    return (
      <Modal
        title={t('payments:summary', 'Payments Summary')}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={800}
      >
        <LoaderContainer>
          <Spin size="large" />
        </LoaderContainer>
      </Modal>
    );
  }

  const columns = [
    {
      title: t('payments:period', 'Period'),
      dataIndex: ['year', 'monthName'],
      key: 'period',
      render: (_: any, record: MonthlyPaymentDto) => `${record.monthName} ${record.year}`,
    },
    {
      title: t('payments:total', 'Total Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: t('payments:paid', 'Paid Amount'),
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: t('payments:pending', 'Pending Amount'),
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      render: (amount: number) => formatCurrency(amount),
    },
  ];

  return (
    <Modal
      title={t('payments:summary', 'Payments Summary')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <div>
        <SummaryTitle>{payments.projectName}</SummaryTitle>

        <SummaryContainer>
          <SummaryItem>
            <SummaryLabel>{t('payments:total', 'Total Amount')}</SummaryLabel>
            <SummaryValue color="#1890ff">{formatCurrency(payments.totalAmount || 0)}</SummaryValue>
          </SummaryItem>

          <SummaryItem>
            <SummaryLabel>{t('payments:paid', 'Paid')}</SummaryLabel>
            <SummaryValue color="#52c41a">{formatCurrency(payments.paidAmount || 0)}</SummaryValue>
          </SummaryItem>

          <SummaryItem>
            <SummaryLabel>{t('payments:pending', 'Pending')}</SummaryLabel>
            <SummaryValue color="#f5222d">{formatCurrency(payments.pendingAmount || 0)}</SummaryValue>
          </SummaryItem>
        </SummaryContainer>

        <CustomTableStyleWrapper>
          <TableWrapper>
            <Table
              dataSource={payments.paymentsByMonth as MonthlyPaymentDto[]}
              columns={columns}
              pagination={false}
              rowKey={record => `${record.year}-${record.month}`}
            />
          </TableWrapper>
        </CustomTableStyleWrapper>
      </div>
    </Modal>
  );
};
